import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, Crown, Settings as SettingsIcon, LogOut } from 'lucide-react';
import { useAuth } from '../auth.jsx';

export default function Navbar() {
  const loc = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const isPremium = user?.plan === 'premium';

  return (
    <header
      style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}
      className="flex items-center gap-4 px-5 h-12 flex-shrink-0 z-10"
    >
      {/* Logo */}
      <Link to="/" className="flex items-center gap-2 no-underline">
        <span
          style={{ background: 'var(--primary)', borderRadius: 7 }}
          className="w-7 h-7 flex items-center justify-center"
        >
          <Zap size={14} color="#0B0E14" strokeWidth={2.5} />
        </span>
        <span
          style={{ fontFamily: 'IBM Plex Mono', color: 'var(--text)', fontWeight: 600 }}
          className="text-sm tracking-tight"
        >
          Corridors
        </span>
      </Link>

      <div style={{ color: 'var(--border-2)' }} className="text-sm select-none">
        /
      </div>

      {/* Dynamic breadcrumb */}
      <nav className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-dim)' }}>
        <Link to="/" style={{ color: 'var(--text-dim)' }} className="hover:text-white transition-colors no-underline">
          Workflows
        </Link>

        {loc.pathname.startsWith('/workflows/') && !loc.pathname.endsWith('/new') && (
          <>
            <span style={{ color: 'var(--border-2)' }}>/</span>
            <span style={{ color: 'var(--text)' }}>
              {loc.pathname.endsWith('/runs') ? 'Runs' : 'Editor'}
            </span>
          </>
        )}

        {loc.pathname === '/workflows/new' && (
          <>
            <span style={{ color: 'var(--border-2)' }}>/</span>
            <span style={{ color: 'var(--text)' }}>New Workflow</span>
          </>
        )}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Plan badge */}
      {user && (
        <Link
          to="/settings"
          className="no-underline"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: isPremium ? 'rgba(234,179,8,.15)' : 'var(--surface-2)',
            color: isPremium ? 'var(--amber)' : 'var(--text-dim)',
            borderRadius: 999, padding: '3px 10px', fontSize: '.72rem', fontWeight: 600,
          }}
        >
          {isPremium && <Crown size={11} />}
          {isPremium ? 'Premium' : 'Free'}
        </Link>
      )}

      {/* User menu */}
      {user && (
        <div className="relative">
          <button
            onClick={() => setMenuOpen(p => !p)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
          >
            {user.avatar_url
              ? <img src={user.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
              : <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', color: '#0B0E14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.8rem' }}>
                  {(user.name || user.email || '?').charAt(0).toUpperCase()}
                </span>}
          </button>

          {menuOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 30 }} onClick={() => setMenuOpen(false)} />
              <div
                style={{
                  position: 'absolute', right: 0, top: '100%', marginTop: 8,
                  background: 'var(--surface-2)', border: '1px solid var(--border)',
                  borderRadius: 10, zIndex: 31, minWidth: 200, boxShadow: '0 8px 32px rgba(0,0,0,.5)',
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ color: 'var(--text)', fontSize: '.82rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user.name || user.email}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '.72rem' }}>{user.email}</div>
                </div>
                <Link
                  to="/settings" onClick={() => setMenuOpen(false)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', color: 'var(--text-dim)', fontSize: '.85rem', textDecoration: 'none' }}
                  className="hover:bg-[#252D3D] transition-colors"
                >
                  <SettingsIcon size={15} /> Settings & billing
                </Link>
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: '.85rem' }}
                  className="hover:bg-red-950 transition-colors"
                >
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
}
